import { Injectable } from '@angular/core';
import {
  HttpInterceptor,
  HttpRequest,
  HttpHandler,
  HttpEvent,
  HttpErrorResponse,
} from '@angular/common/http';
import { Observable, from, throwError } from 'rxjs';
import { switchMap, catchError } from 'rxjs/operators';
import { AuthService } from '../auth/auth.service';

@Injectable()
export class AuthInterceptor implements HttpInterceptor {
  private readonly EXCLUDED_URLS: string[] = [
    '/assets/',
    '/favicon',
  ];
  private refreshPromise: Promise<string | null> | null = null;

  constructor(private authService: AuthService) { }

  intercept(
    request: HttpRequest<any>,
    next: HttpHandler
  ): Observable<HttpEvent<any>> {
    if (this.isExcludedUrl(request.url)) {
      return next.handle(request);
    }

    return from(this.resolveValidToken()).pipe(
      switchMap((token) => {
        const authReq = token ? this.attachToken(request, token) : request;

        return next.handle(authReq).pipe(
          catchError((error: HttpErrorResponse) => {
            if (error.status === 401) {
              return from(this.attemptRefresh()).pipe(
                switchMap((newToken) => {
                  if (!newToken) {
                    this.authService.onAcquireTokenSilentError();
                    return throwError(() => error);
                  }
                  if (request.headers.has('x-retry')) {
                    return throwError(() => error);
                  }
                  const retryReq = this.attachToken(request, newToken).clone({
                    setHeaders: { 'x-retry': 'true' }
                  });
                  return next.handle(retryReq);
                })
              );
            }
            return throwError(() => error);
          })
        );
      })
    );
  }

  private async resolveValidToken(): Promise<string | null> {
    const detailToken = this.authService.detailToken;
    const expiresOn = detailToken?.expiresOn ?? localStorage.getItem('expiresOn');
    const token = detailToken?.accessToken ?? localStorage.getItem('token');

    if (!expiresOn) {
      return token;
    }

    const expirationTime = new Date(expiresOn).getTime();
    const msUntilExpiry = expirationTime - Date.now();

    if (
      !Number.isFinite(expirationTime) ||
      msUntilExpiry <= this.authService.intervalRefresh
    ) {
      return this.attemptRefresh();
    }

    return token;
  }

  private async attemptRefresh(): Promise<string | null> {
    if (!this.refreshPromise) {
      this.refreshPromise = this.authService.refreshTokenIfNeeded()
        .finally(() => {
          this.refreshPromise = null;
        });
    }

    return this.refreshPromise;
  }

  private attachToken(
    request: HttpRequest<any>,
    token: string
  ): HttpRequest<any> {
    return request.clone({
      setHeaders: { Authorization: `Bearer ${token}` },
    });
  }

  private isExcludedUrl(url: string): boolean {
    return this.EXCLUDED_URLS.some((excluded) => url.includes(excluded));
  }
}
